import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, IconButton, Box, Chip, LinearProgress, Tooltip,
  Button, ButtonGroup, TableSortLabel
} from '@mui/material';
import {
  KeyboardArrowDown, KeyboardArrowUp,
  UnfoldMore, UnfoldLess,
  Visibility, VisibilityOff,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import AppContext from '../AppContext';
import { getResumJerarquic, resolveConcepteName } from '../../services/concepteGraphService';
import { formatEuro } from '../../functions/formatNumber';
import { getColorForAmount } from '../../functions/colors';
import { ConcepteIcon } from './IconMap';

const nodeHasInversio = (n) => {
  if (n.tipusFlux === 'INVERSIO' || n.agrupacio === 'INVERSIONS') return true;
  if (n.fills && Array.isArray(n.fills) && n.fills.length > 0) {
    return n.fills.some(nodeHasInversio);
  }
  return false;
};

// Càlcul recursiu dels moviments pendents de reclassificar per a tot l'arbre sota un node
const getReclassificationStats = (node) => {
  if (!node || !node.fills || !Array.isArray(node.fills) || node.fills.length === 0) {
    return {
      directPending: 0,
      descendantPending: 0,
      totalPending: 0
    };
  }

  const sumChildrenMoviments = node.fills.reduce((acc, f) => acc + (f.numMoviments || 0), 0);
  const directPending = Math.max(0, (node.numMoviments || 0) - sumChildrenMoviments);

  let descendantPending = 0;
  node.fills.forEach(child => {
    const childStats = getReclassificationStats(child);
    descendantPending += childStats.totalPending;
  });

  return {
    directPending,
    descendantPending,
    totalPending: directPending + descendantPending
  };
};

// Poda recursiva: elimina de l'arbre els conceptes que no tenen cap moviment ni ells ni cap dels seus fills
const filterTreeWithMoviments = (nodes) => {
  if (!nodes || !Array.isArray(nodes)) return [];

  return nodes
    .map(node => {
      const filteredFills = node.fills && Array.isArray(node.fills)
        ? filterTreeWithMoviments(node.fills)
        : [];

      const selfMoviments = node.numMoviments || 0;
      const hasChildrenMovs = filteredFills.length > 0;

      // Si ni el node té moviments (> 0) ni cap dels seus fills té moviments, es descarta
      if (selfMoviments <= 0 && !hasChildrenMovs) {
        return null;
      }

      return {
        ...node,
        fills: filteredFills
      };
    })
    .filter(Boolean);
};

// Càlcul del saldo net tenint en compte els signes
const getNodeSaldo = (n) => {
  if (n.saldo !== undefined && n.saldo !== null) return n.saldo;
  const ingres = n.totalIngres || 0;
  const despesa = Math.abs(n.totalDespesa || 0);
  return ingres - despesa;
};

// Funció genèrica d'ordenació per a qualsevol clau i direcció
const sortHierarchyNodes = (nodes, sortConfig) => {
  if (!nodes || !Array.isArray(nodes)) return [];
  const { key, direction } = sortConfig;
  const dir = direction === 'asc' ? 1 : -1;

  return [...nodes].sort((a, b) => {
    let valA, valB;
    switch (key) {
      case 'concepte':
        return dir * (a.nom || '').localeCompare(b.nom || '');
      case 'numMoviments':
        valA = a.numMoviments || 0;
        valB = b.numMoviments || 0;
        return dir * (valA - valB);
      case 'pes':
        valA = typeof a.pes === 'number' ? a.pes : 0;
        valB = typeof b.pes === 'number' ? b.pes : 0;
        return dir * (valA - valB);
      case 'mitja':
        valA = a.mitjaSaldo !== undefined && a.mitjaSaldo !== null ? a.mitjaSaldo : (a.mitja || 0);
        valB = b.mitjaSaldo !== undefined && b.mitjaSaldo !== null ? b.mitjaSaldo : (b.mitja || 0);
        return dir * (valA - valB);
      case 'desviamentMitja':
        valA = (a.variacioMitjaAnys && typeof a.variacioMitjaAnys === 'object') ? (a.variacioMitjaAnys.saldo || 0) : (a.variacioMitjaAnys || 0);
        valB = (b.variacioMitjaAnys && typeof b.variacioMitjaAnys === 'object') ? (b.variacioMitjaAnys.saldo || 0) : (b.variacioMitjaAnys || 0);
        return dir * (valA - valB);
      case 'desviamentAnyAnt':
        valA = (a.variacioAnyAnterior && typeof a.variacioAnyAnterior === 'object') ? (a.variacioAnyAnterior.saldo || 0) : (a.variacioAnyAnterior || 0);
        valB = (b.variacioAnyAnterior && typeof b.variacioAnyAnterior === 'object') ? (b.variacioAnyAnterior.saldo || 0) : (b.variacioAnyAnterior || 0);
        return dir * (valA - valB);
      case 'saldo':
      default:
        valA = getNodeSaldo(a);
        valB = getNodeSaldo(b);
        return dir * (valA - valB);
    }
  });
};

const HierarchyRow = ({
  node,
  level = 1,
  parentConcept = null,
  userapp,
  anyo,
  mes,
  tipus = 'anual',
  maxDespesa = 1,
  totalDespesaGlobal = 1,
  viewMode = 'actual',
  conceptesList = [],
  conceptesTree = [],
  expandedKeys,
  onToggle,
  showDetails = false,
  sortConfig = { key: 'saldo', direction: 'asc' }
}) => {
  const navigate = useNavigate();

  // Ordenar fills segons la configuració d'ordenació
  const sortedFills = useMemo(() => {
    return sortHierarchyNodes(node.fills, sortConfig);
  }, [node.fills, sortConfig]);

  const nodeKey = node.conceptePath || node.path || `${node.id}_${level}` || node.codi;
  const hasChildren = node.fills && node.fills.length > 0;
  const isOpen = expandedKeys.has(nodeKey);

  const despesa = Math.abs(node.totalDespesa || 0);
  const ingres = node.totalIngres || 0;
  const saldo = node.saldo !== undefined ? node.saldo : (ingres - despesa);
  const colorSaldo = getColorForAmount(saldo, node.tipusFlux === 'INVERSIO');
  const percentDespesa = maxDespesa > 0 ? Math.min(100, Math.round((despesa / maxDespesa) * 100)) : 0;

  // Filtrar per viewMode
  const isInversio = node.tipusFlux === 'INVERSIO' || node.agrupacio === 'INVERSIONS';
  const containsInversio = nodeHasInversio(node);

  if (viewMode === 'inversiones' && !containsInversio) {
    return null;
  }
  if (viewMode === 'estalvi' && isInversio) {
    return null;
  }

  // Càlcul de moviments pendents de reclassificar (directes i en subcategories)
  const reclassStats = getReclassificationStats(node);
  const hasUnclassified = reclassStats.totalPending > 0;
  const unclassifiedTooltip = (() => {
    if (!hasUnclassified) return '';
    const { directPending, descendantPending } = reclassStats;
    if (directPending > 0 && descendantPending > 0) {
      return `${directPending} moviments directes i ${descendantPending} en subcategories pendents de reclassificar`;
    }
    if (directPending > 0) {
      return `${directPending} moviments assignats directament a ${node.nom} pendents de reclassificar als fills més concrets`;
    }
    return `${descendantPending} moviments pendents de reclassificar en subcategories (desplegueu per veure-ho)`;
  })();

  // Càlcul de Pes
  const pesPercent = (() => {
    if (node.pes !== undefined && node.pes !== null) {
      if (typeof node.pes === 'string') {
        return node.pes.endsWith('%') ? node.pes : `${node.pes}%`;
      }
      if (typeof node.pes === 'number') {
        return `${Math.round(node.pes * 10) / 10}%`;
      }
      return `${node.pes}%`;
    }
    return totalDespesaGlobal > 0
      ? `${Math.round((despesa / totalDespesaGlobal) * 100)}%`
      : '0%';
  })();

  // Mètriques clàssiques
  const mitjaValor = node.mitjaSaldo !== undefined
    ? node.mitjaSaldo
    : (node.mitja !== undefined ? node.mitja : null);

  const desvMitjaValor = (node.variacioMitjaAnys && typeof node.variacioMitjaAnys === 'object')
    ? node.variacioMitjaAnys.saldo
    : (node.variacioMitjaAnys !== undefined && node.variacioMitjaAnys !== null ? node.variacioMitjaAnys : null);

  const desvAnyAntValor = (node.variacioAnyAnterior && typeof node.variacioAnyAnterior === 'object')
    ? node.variacioAnyAnterior.saldo
    : (node.variacioAnyAnterior !== undefined && node.variacioAnyAnterior !== null ? node.variacioAnyAnterior : null);

  const handleNavigate = () => {
    const p = node.conceptePath || node.path;
    const pathQuery = p ? `?path=${encodeURIComponent(p)}` : '';

    if (node.id && node.id > 0) {
      if (tipus === 'mensual' && anyo && mes) {
        navigate(`/${userapp}/dashboard/${anyo}/mes/${mes}/node/${node.id}${pathQuery}`);
      } else if (tipus === 'anual' && anyo) {
        navigate(`/${userapp}/dashboard/${anyo}/node/${node.id}${pathQuery}`);
      } else {
        navigate(`/${userapp}/dashboard/node/${node.id}${pathQuery}`);
      }
      return;
    }

    const resolvedName = node.concepteClassic || resolveConcepteName(node.nom, conceptesList, conceptesTree) || node.nom;

    if (tipus === 'mensual' && anyo && mes) {
      navigate(`/${userapp}/dashboard/${anyo}/mes/${mes}/concepte/${encodeURIComponent(resolvedName)}`);
    } else if (tipus === 'anual' && anyo) {
      navigate(`/${userapp}/dashboard/${anyo}/concepte/${encodeURIComponent(resolvedName)}`);
    } else {
      navigate(`/${userapp}/dashboard/concepte/${encodeURIComponent(resolvedName)}`);
    }
  };

  const getNodeIcon = () => {
    return (
      <ConcepteIcon
        code={node.codi}
        concept={node.nom}
        fallback={[node.concepteClassic, parentConcept]}
        tipusNode={node.tipusNode}
        level={level}
        parentConcept={parentConcept}
        onlyIcon
        sx={{ fontSize: 18 }}
      />
    );
  };

  const indentPx = (level - 1) * 22 + 8;

  return (
    <>
      <TableRow
        hover
        onClick={handleNavigate}
        sx={{
          cursor: 'pointer',
          backgroundColor: level === 1 ? '#fafafa' : level === 2 ? '#ffffff' : '#fcfcfc',
          '& > *': { borderBottom: '1px solid #edf0f2' }
        }}
      >
        {/* Columna 1: Categoria / Concepte */}
        <TableCell sx={{ pl: `${indentPx}px`, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(nodeKey);
                }}
                sx={{ p: 0.2 }}
              >
                {isOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 24 }} />
            )}
            {getNodeIcon()}
            <Typography
              variant="body2"
              sx={{
                fontWeight: level === 1 ? 'bold' : level === 2 ? 600 : 'normal',
                color: level === 1 ? 'primary.main' : 'text.primary'
              }}
            >
              {node.nom}
            </Typography>
            {node.concepteClassic && (
              <Tooltip title={`Mapejat a concepte clàssic: ${node.concepteClassic}`}>
                <Chip
                  label={node.concepteClassic}
                  size="small"
                  sx={{
                    fontSize: '0.60rem',
                    height: 18,
                    bgcolor: '#f5f5f5',
                    color: '#555',
                    border: '1px dashed #bdbdbd'
                  }}
                />
              </Tooltip>
            )}
            {node.tipusNode && node.tipusNode !== 'CATEGORIA' && (
              <Chip
                label={node.tipusNode}
                size="small"
                sx={{
                  fontSize: '0.62rem',
                  height: 18,
                  bgcolor: node.tipusNode === 'VEHICLE' ? '#e1f5fe' : '#f3e5f5'
                }}
              />
            )}
          </Box>
        </TableCell>

        {/* Columna 2: Núm. Moviments (Alineat a la dreta amb indicador d'avís de reclassificació) */}
        <TableCell align="right" sx={{ width: 105, py: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Chip label={node.numMoviments || 0} size="small" sx={{ fontSize: '0.68rem', height: 19 }} />
            {hasUnclassified && (
              <Tooltip title={unclassifiedTooltip}>
                <WarningAmberIcon color="warning" sx={{ fontSize: 16, cursor: 'help' }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>

        {/* Columnes de Detalls (Pes, Mitja, Desviacions) */}
        {showDetails && (
          <>
            {/* Columna 3: Pes (%) */}
            <TableCell align="right" sx={{ width: 100, py: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: level === 1 ? 600 : 'normal' }}>
                {pesPercent}
              </Typography>
              {despesa > 0 && (
                <LinearProgress
                  variant="determinate"
                  value={percentDespesa}
                  sx={{ height: 3, borderRadius: 1.5, mt: 0.3, bgcolor: '#f0f0f0' }}
                  color="error"
                />
              )}
            </TableCell>

            {/* Columna 4: Mitjana (mida reduïda) */}
            <TableCell align="right" sx={{ width: 110, py: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {mitjaValor !== null ? formatEuro(mitjaValor) : '-'}
              </Typography>
            </TableCell>

            {/* Columna 5: Desviament Mitjana (mida reduïda) */}
            <TableCell align="right" sx={{ width: 120, py: 1 }}>
              {desvMitjaValor !== null ? (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: getColorForAmount(desvMitjaValor) }}>
                  {formatEuro(desvMitjaValor)}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                  -
                </Typography>
              )}
            </TableCell>

            {/* Columna 6: Desviament Any Anterior (mida reduïda) */}
            <TableCell align="right" sx={{ width: 120, py: 1 }}>
              {desvAnyAntValor !== null ? (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: getColorForAmount(desvAnyAntValor) }}>
                  {formatEuro(desvAnyAntValor)}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                  -
                </Typography>
              )}
            </TableCell>
          </>
        )}

        {/* Columna 7: Saldo Net / Despesa (Últim valor amb màxim protagonisme) */}
        <TableCell align="right" sx={{ width: 140, py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: level === 1 ? '0.95rem' : '0.88rem', color: colorSaldo }}>
            {formatEuro(saldo)}
          </Typography>
          {ingres > 0 && despesa > 0 && (
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary' }}>
              +{formatEuro(ingres)} / -{formatEuro(despesa)}
            </Typography>
          )}
        </TableCell>
      </TableRow>

      {/* Renderitzat de fills pla a la mateixa graella per a una alineació matemàticament perfecta */}
      {hasChildren && isOpen && sortedFills.map((childNode) => (
        <HierarchyRow
          key={childNode.id || childNode.codi}
          node={childNode}
          level={level + 1}
          parentConcept={node.nom}
          userapp={userapp}
          anyo={anyo}
          mes={mes}
          tipus={tipus}
          maxDespesa={despesa > 0 ? despesa : maxDespesa}
          totalDespesaGlobal={totalDespesaGlobal}
          viewMode={viewMode}
          conceptesList={conceptesList}
          conceptesTree={conceptesTree}
          expandedKeys={expandedKeys}
          onToggle={onToggle}
          showDetails={showDetails}
          sortConfig={sortConfig}
        />
      ))}
    </>
  );
};

const ConceptHierarchyList = ({
  userapp,
  anyo = null,
  mes = null,
  tipus = 'anual',
  viewMode = 'actual'
}) => {
  const { ctxUser, conceptesList, conceptesTree } = useContext(AppContext);
  const jwtToken = ctxUser?.fdvToken?.jwtToken;
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState(new Set()); // Per defecte tot col·lapsat
  const [showDetails, setShowDetails] = useState(false); // Ocults per defecte
  const [sortConfig, setSortConfig] = useState({ key: 'saldo', direction: 'asc' }); // Per defecte ascendent per saldo (primer més costosos)

  useEffect(() => {
    if (!userapp) return;
    setLoading(true);
    const targetMes = tipus === 'mensual' ? (mes || undefined) : undefined;
    getResumJerarquic(jwtToken, userapp, anyo || undefined, targetMes)
      .then((data) => {
        setTreeData(data || []);
        // Per defecte tot col·lapsat: expandedKeys comença com a buit
        setExpandedKeys(new Set());
      })
      .catch((err) => {
        console.warn('Error al carregar resum jeràrquic, fallback a arbre bàsic:', err);
        setTreeData(conceptesTree || []);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userapp, jwtToken, anyo, mes, tipus, conceptesTree]);

  // Recollir totes les claus desplegables per a Expandir Tot
  const collectAllExpandableKeys = (nodes, lvl = 1) => {
    const keys = new Set();
    const walk = (items, l) => {
      if (!items || !Array.isArray(items)) return;
      items.forEach(n => {
        if (n.fills && n.fills.length > 0) {
          const k = n.conceptePath || n.path || `${n.id}_${l}` || n.codi;
          keys.add(k);
          walk(n.fills, l + 1);
        }
      });
    };
    walk(nodes, lvl);
    return keys;
  };

  const handleExpandAll = () => {
    setExpandedKeys(collectAllExpandableKeys(sortedTreeData));
  };

  const handleCollapseAll = () => {
    setExpandedKeys(new Set());
  };

  const handleToggle = (key) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Filtrar i ordenar els conceptes jeràrquics per als dashboards (Global, Anual, Mensual):
  // 1. Es descarten els blocs transversals globals de Persones i Cotxes/Vehicles (només es mostren a Resum Jeràrquic)
  // 2. Es descarten els conceptes que no tenen cap moviment ni ells ni cap dels seus fills
  const sortedTreeData = useMemo(() => {
    const withoutEntities = (treeData || []).filter(node => {
      if (node.tipusNode === 'PERSONA' || node.tipusNode === 'VEHICLE') return false;
      if (node.id === -100 || node.id === -200) return false;
      if (node.codi === 'ENT_PERSONES' || node.codi === 'ENT_VEHICLES') return false;
      if (node.codi === 'VIRTUAL_PERSONES' || node.codi === 'VIRTUAL_VEHICLES') return false;
      if (node.nom === 'Persones' || node.nom === 'Vehicles') return false;
      return true;
    });

    const withMovimentsOnly = filterTreeWithMoviments(withoutEntities);

    return sortHierarchyNodes(withMovimentsOnly, sortConfig);
  }, [treeData, sortConfig]);

  const totalDespesaGlobal = useMemo(() => {
    return sortedTreeData.reduce((acc, n) => acc + Math.abs(n.totalDespesa || 0), 0) || 1;
  }, [sortedTreeData]);

  return (
    <Paper elevation={0} sx={{ p: 1, borderTop: '1px solid #eee' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <ButtonGroup size="small" variant="outlined">
          <Button startIcon={<UnfoldMore />} onClick={handleExpandAll} sx={{ fontSize: '0.72rem' }}>
            Expandir tot
          </Button>
          <Button startIcon={<UnfoldLess />} onClick={handleCollapseAll} sx={{ fontSize: '0.72rem' }}>
            Col·lapsar tot
          </Button>
        </ButtonGroup>

        <Button
          size="small"
          variant={showDetails ? 'contained' : 'outlined'}
          color="secondary"
          startIcon={showDetails ? <VisibilityOff /> : <Visibility />}
          onClick={() => setShowDetails(prev => !prev)}
          sx={{ fontSize: '0.72rem' }}
        >
          {showDetails ? 'Ocultar detalls' : 'Mostrar detalls'}
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <TableContainer sx={{ maxHeight: '65vh' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#f4f6f8', fontWeight: 'bold', fontSize: '0.82rem' } }}>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'concepte'}
                  direction={sortConfig.key === 'concepte' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('concepte')}
                >
                  Categoria / Concepte
                </TableSortLabel>
              </TableCell>

              <TableCell align="right" sx={{ width: 105 }}>
                <TableSortLabel
                  active={sortConfig.key === 'numMoviments'}
                  direction={sortConfig.key === 'numMoviments' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('numMoviments')}
                >
                  Moviments
                </TableSortLabel>
              </TableCell>

              {showDetails && (
                <>
                  <TableCell align="right" sx={{ width: 100 }}>
                    <TableSortLabel
                      active={sortConfig.key === 'pes'}
                      direction={sortConfig.key === 'pes' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('pes')}
                    >
                      Pes
                    </TableSortLabel>
                  </TableCell>

                  <TableCell align="right" sx={{ width: 110, fontSize: '0.78rem' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'mitja'}
                      direction={sortConfig.key === 'mitja' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('mitja')}
                    >
                      Mitja
                    </TableSortLabel>
                  </TableCell>

                  <TableCell align="right" sx={{ width: 120, fontSize: '0.78rem' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'desviamentMitja'}
                      direction={sortConfig.key === 'desviamentMitja' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('desviamentMitja')}
                    >
                      Desv. Mitjana
                    </TableSortLabel>
                  </TableCell>

                  <TableCell align="right" sx={{ width: 120, fontSize: '0.78rem' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'desviamentAnyAnt'}
                      direction={sortConfig.key === 'desviamentAnyAnt' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('desviamentAnyAnt')}
                    >
                      Any Anterior
                    </TableSortLabel>
                  </TableCell>
                </>
              )}

              <TableCell align="right" sx={{ width: 140, fontWeight: 'bold' }}>
                <TableSortLabel
                  active={sortConfig.key === 'saldo'}
                  direction={sortConfig.key === 'saldo' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('saldo')}
                >
                  Saldo
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTreeData.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={showDetails ? 7 : 3} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">
                    No hi ha dades jeràrquiques disponibles per a aquest període.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {sortedTreeData.map((node) => (
              <HierarchyRow
                key={node.id || node.codi}
                node={node}
                level={1}
                parentConcept={null}
                userapp={userapp}
                anyo={anyo}
                mes={mes}
                tipus={tipus}
                maxDespesa={totalDespesaGlobal}
                totalDespesaGlobal={totalDespesaGlobal}
                viewMode={viewMode}
                conceptesList={conceptesList}
                conceptesTree={conceptesTree}
                expandedKeys={expandedKeys}
                onToggle={handleToggle}
                showDetails={showDetails}
                sortConfig={sortConfig}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ConceptHierarchyList;
